import { AnimatePresence, motion } from "framer-motion";
import { Globe2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import WhatsAppIcon from "@/components/site/icons/WhatsAppIcon";
import { SectionHead, TextField, FieldLabel, FieldError } from "@/components/inquiry/inquiryFields";
import { formatPhone, detectCountryFromPhone } from "@/lib/phoneCountry";

/**
 * StepContact — name, email, WhatsApp/Instagram + preferred channel.
 */
export const StepContact = ({ data, onField, errors }) => {
    const onPhoneChange = (raw) => {
        const formatted = formatPhone(raw);
        const detected = detectCountryFromPhone(formatted);
        onField("whatsapp_number", formatted);
        onField("detected_country", detected.name || "");
        onField("detected_country_iso", detected.iso || "");
    };

    return (
        <section data-testid="step-contact">
            <SectionHead
                eyebrow="Step 07 · Contact"
                title="How should we reach you?"
                description="Share at least one direct channel — WhatsApp or Instagram — plus your email. Pranto replies personally within 24h."
            />

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <TextField
                    id="contact-full-name"
                    label="Full Name"
                    required
                    value={data.full_name}
                    onChange={(e) => onField("full_name", e.target.value)}
                    placeholder="Your full name"
                    testId="contact-full-name-input"
                    autoComplete="name"
                    error={errors?.full_name}
                />

                <TextField
                    id="contact-email"
                    label="Email Address"
                    type="email"
                    required
                    value={data.email}
                    onChange={(e) => onField("email", e.target.value)}
                    placeholder="you@brand.com"
                    testId="contact-email-input"
                    autoComplete="email"
                    error={errors?.email}
                />

                {/* WhatsApp */}
                <div className="flex flex-col gap-1.5">
                    <FieldLabel htmlFor="contact-whatsapp">
                        <span className="inline-flex items-center gap-1.5">
                            <WhatsAppIcon className="h-3 w-3" style={{ color: "var(--pg-accent)" }} />
                            WhatsApp Number
                        </span>
                    </FieldLabel>
                    <div className="relative">
                        <span
                            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
                            style={{ color: "var(--pg-accent)" }}
                        >
                            <WhatsAppIcon className="h-4 w-4" />
                        </span>
                        <input
                            id="contact-whatsapp"
                            type="tel"
                            value={data.whatsapp_number}
                            onChange={(e) => onPhoneChange(e.target.value)}
                            placeholder="+1 555 000 1234"
                            data-testid="contact-whatsapp-input"
                            autoComplete="tel"
                            className="pg-input pl-10"
                        />
                    </div>
                    <AnimatePresence>
                        {data.detected_country && (
                            <motion.p
                                initial={{ opacity: 0, y: -4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                className="flex items-center gap-1.5 text-[11px]"
                                style={{ color: "var(--pg-accent)" }}
                                data-testid="contact-detected-country"
                            >
                                <Globe2 className="h-3 w-3" /> Detected: {data.detected_country}
                            </motion.p>
                        )}
                    </AnimatePresence>
                </div>

                <TextField
                    id="contact-instagram"
                    label="Instagram Username"
                    value={data.instagram_username}
                    onChange={(e) => onField("instagram_username", e.target.value)}
                    placeholder="@yourhandle"
                    testId="contact-instagram-input"
                    hint="WhatsApp or Instagram — at least one is required."
                />

                <div className="flex flex-col gap-1.5">
                    <FieldLabel htmlFor="contact-preferred">Preferred Channel</FieldLabel>
                    <Select value={data.preferred_communication || undefined} onValueChange={(v) => onField("preferred_communication", v)}>
                        <SelectTrigger
                            id="contact-preferred"
                            className="h-12 rounded-xl pg-input"
                            style={{ background: "var(--pg-bg)" }}
                            data-testid="contact-preferred-select"
                        >
                            <SelectValue placeholder="Pick one" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                            <SelectItem value="Instagram">Instagram</SelectItem>
                            <SelectItem value="Email">Email</SelectItem>
                        </SelectContent>
                    </Select>
                    <FieldError error={errors?.contact} />
                </div>
            </div>
        </section>
    );
};

export default StepContact;
